// QuotationPDF - the quotation document rendered with @react-pdf/renderer.
// Used by crmQuotationCreation.jsx for the step 2 <PDFViewer> preview and for pdf(...).toBlob() on submit
// (that Blob is uploaded and its URL sent on WhatsApp). Prop `data` = invoiceData built in that page.

import {
    Page,
    Text,
    View,
    Document,
    StyleSheet, Image
} from "@react-pdf/renderer";
import touchLineLogo from '../../../assests/touchLineLogo.jpeg';
import redFlagLogo from '../../../assests/touchLineRedFlagLogo.jpg';


import { Font } from "@react-pdf/renderer";

// NotoSans is registered so the rupee sign renders (Helvetica appears not to include it); used for price columns.
Font.register({
    family: "NotoSans",
    src: "https://fonts.gstatic.com/s/notosans/v27/o-0IIpQlx3QUlC5A4PNb4j5Ba_2c7A.ttf",
});


// PDF styles. Note: companyTitle and companyInfo are defined twice; the later definitions win.
const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontSize: 11,
        fontFamily: "Helvetica",
    },
    headerRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginVertical: 10,
        width: "100%",
    },

    leftLogo: {
        width: 120,
        height: 90,
    },

    rightLogo: {
        width: 120,
        height: 100,
    },

    centerContent: {
        flex: 1,
        paddingHorizontal: 10,
        alignItems: "center",
    },

    companyTitle: {
        fontSize: 20,
        fontWeight: "bold",
        color: "red",
        textAlign: "center",
    },

    companyInfo: {
        fontSize: 13,
        textAlign: "center",
        marginTop: 5,
        lineHeight: 18,
    },
    companyTitle: {
        textAlign: "center",
        color: "red",
        fontSize: 24,
        fontWeight: "bold",
    },

    companyInfo: {
        textAlign: "center",
        fontSize: 10,
        marginBottom: 15,
        lineHeight: 1.3,
    },

    rowBetween: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 8,
    },

    subjectText: {
        marginTop: 15,
        lineHeight: 1.4
    },

    tableHeader: {
        marginTop: 15,
        flexDirection: "row",
        borderBottomWidth: 1,
        paddingVertical: 6,
        backgroundColor: "#eaeaea",
        width: "100%"
    },

    tableRow: {
        flexDirection: "row",
        paddingVertical: 6,
        borderBottomWidth: 0,
    },

    col1: { width: "10%", textAlign: "center" },
    col2: { width: "40%", paddingRight: 6 },
    col3: { width: "10%", textAlign: "center" },
    col4: { width: "20%", textAlign: "right" },
    col5: { width: "20%", textAlign: "right" },

    totalRow1: {
        flexDirection: "row",
        paddingVertical: 8,
        backgroundColor: "#F3E5F5",
        borderTopWidth: 1,
    },

    totalLabel: {
        width: "40%",
        fontWeight: "bold"
    },
    featureLine: {
        fontSize: 10,
        marginLeft: 10,
        marginBottom: 2,
        fontWeight: "bold",
    },

    sectionTitle: {
        marginTop: 20,
        fontWeight: "bold",
    },

    termLine: {
        fontSize: 10,
        marginTop: 3,
    },

    signature: {
        marginTop: 20,
        fontSize: 11,
        lineHeight: 1.4,
    },
});


// Single A4 page (wrap={false}). Each item row shows the first line of item_desc (before "•"),
// qty, rate (itemPrice) and amount (net price), followed by a totals row.
const QuotationPDF = ({ data }) => (
    <Document>
        <Page size="A4" style={styles.page} wrap={false}>

            {/* COMPANY HEADER */}
            <View style={styles.headerRow}>

                {/* Left Logo */}
                <Image
                    source={touchLineLogo}
                    style={styles.leftLogo}
                    resizeMode="contain"
                />

                {/* Center Company Details */}
                <View style={styles.centerContent}>
                    <Text style={styles.companyTitle}>{data.companyName}</Text>

                    <Text style={styles.companyInfo}>
                        {data.companyAddress}{"\n"}
                        Tel: +91{data.mobile1}, {data.mobile2}{"\n"}
                        Website: {data.website}{"\n"}
                        Email: {data.email}
                    </Text>
                </View>

                {/* Right Logo */}
                <Image
                    source={redFlagLogo}
                    style={styles.rightLogo}
                    resizeMode="contain"
                />
            </View>


            {/* REFERENCE & DATE */}
            <View style={styles.rowBetween}>
                <Text>Ref: {data.ref}</Text>
                <Text>Date: {data.date}</Text>
            </View>

            {/* RECEIVER */}
            <View style={{ flexDirection: "row" }}>
                <View style={{ flex: 1 }}>
                    <Text>
                        To,{"\n"}
                        {data.receiverName}{"\n"}
                        {data.receiverCity}
                    </Text>
                </View>

                <View style={{ flex: 1 }}>
                </View>
            </View>


            {/* SUBJECT */}
            <Text style={styles.subjectText}>
                Dear Sir,{"\n"}
                Sub: {data.subject}
            </Text>

            <Text>
                Thank you very much for your enquiry for your requirement of TOUCH LINE Interactive Flat Panel, We are pleased to submit our best
                offer for the same for your kind consideration
            </Text>

            {/* TABLE HEADER */}
            <View style={styles.tableHeader}>
                <Text style={styles.col1}>S.No</Text>
                <Text style={styles.col2}>Item & Size</Text>
                <Text style={styles.col3}>Qty</Text>
                <Text style={styles.col4}>Rate</Text>
                <Text style={styles.col5}>Amount</Text>
            </View>

            {/* ITEMS LOOP */}
            {data.items.map((item, idx) => {
                const lines = item.title.split("\r").map(x => x.trim()).filter(Boolean);
                const mainTitle = lines[0] || "";

                return (
                    <View style={styles.tableRow} key={idx}>
                        <Text style={styles.col1}>{idx + 1}</Text>

                        <View style={styles.col2}>
                            <Text style={{ fontWeight: "bold" }}>{mainTitle.split("•")[0]}</Text>
                        </View>

                        <Text style={styles.col3}>{item.qty}</Text>

                        <Text style={{ ...styles.col4, fontFamily: "NotoSans" }}>
                            ₹ {Number(item.rate).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </Text>

                        <Text style={{ ...styles.col5, fontFamily: "NotoSans" }}>
                            ₹ {Number(item.ammount).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </Text>
                    </View>
                );
            })}

            {/* TOTAL ROW */}
            <View style={styles.totalRow1}>
                <Text style={styles.col1}></Text>

                <Text style={styles.totalLabel}>TOTAL</Text>

                {/* TOTAL QTY */}
                <Text style={styles.col3}>
                    {data.items.reduce((sum, item) => sum + Number(item.qty), 0)}
                </Text>

                {/* TOTAL RATE */}
                <Text style={{ ...styles.col4, fontFamily: "NotoSans" }}>
                    ₹ {
                        data.items
                            .reduce((sum, item) => sum + Number(item.rate), 0)
                            .toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })
                    }
                </Text>

                {/* TOTAL AMOUNT */}
                <Text style={{ ...styles.col5, fontFamily: "NotoSans" }}>
                    ₹ {
                        data.items
                            .reduce((sum, item) => sum + Number(item.ammount), 0)
                            .toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })
                    }
                </Text>
            </View>

            {/* Bullet feature list from the FIRST product template only (index 0), not per selected item. */}
            <View style={styles.col2}>
                {data.productDetails.map((item, index) => (
                    <Text
                        key={index}
                        style={{ marginBottom: 3, marginLeft: 20, marginTop: 5 }}
                    >
                        {index === 0 &&
                            item.item_desc
                                .split("•")
                                .slice(1)
                                .map((text) => `\n•${text}`)
                                .join("")}
                    </Text>
                ))}
            </View>

            {/* TERMS & CONDITIONS */}
            <Text style={{ marginTop: 10, lineHeight: 1 }}>
                {/* Strips the simple HTML in the terms string down to plain text. */}
                {data?.terms
                    .replace(/<br\s*\/?>/gi, "\n")
                    .replace(/<\/p>/gi, "\n\n")
                    .replace(/<p>/gi, "")
                    .replace(/<b>(.*?)<\/b>/gi, "$1")   // Remove <b> tags, keep content
                    .replace(/^\s+/gm, "")              // Remove leading spaces on each line
                    .replace(/<[^>]+>/g, "")}
            </Text>

            {/* SIGNATURE */}
            <Text style={styles.signature}>
                Yours Truly,{"\n"}
                {/* For {data.companyName},{"\n"} */}
                For  <Text style={{ fontFamily: "Helvetica-Bold" }}>{data.companyName},{"\n"}Sd/-{"\n"}</Text>
                {data.signedBy} {"\n"}
                {data.mobile1}
            </Text>
        </Page>
    </Document>
);
export default QuotationPDF;
